import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { retryNon4xx } from "shared/api/query-retry";
import { collectionsAdminApi } from "../../api/collectionsAdminApi";
var KEYS = {
    all: ["admin", "collections"],
    filtered: function (filters) {
        return ["admin", "collections", "list", filters !== null && filters !== void 0 ? filters : {}];
    },
    one: function (id) { return ["admin", "collections", id]; },
};
export var useAdminCollections = function (filters) {
    return useQuery({
        queryKey: KEYS.filtered(filters),
        queryFn: function () { return collectionsAdminApi.getAll(filters); },
        staleTime: 30000,
        retry: retryNon4xx,
    });
};
// Серверная пагинация для таблицы списка коллекций (page/limit + meta).
export var useAdminCollectionsPaged = function (filters, page, limit) {
    if (limit === void 0) { limit = 12; }
    return useQuery({
        queryKey: ["admin", "collections", "paged", filters !== null && filters !== void 0 ? filters : {}, page, limit],
        queryFn: function () { return collectionsAdminApi.getPaged(filters, page, limit); },
        staleTime: 30000,
        retry: retryNon4xx,
    });
};
export var useAdminCollection = function (id) {
    return useQuery({
        queryKey: KEYS.one(id),
        queryFn: function () { return collectionsAdminApi.getById(id); },
        enabled: !!id,
        staleTime: 30000,
        retry: retryNon4xx,
    });
};
export var useCreateCollection = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (payload) { return collectionsAdminApi.create(payload); },
        onSuccess: function () { return qc.invalidateQueries({ queryKey: KEYS.all }); },
    });
};
export var useUpdateCollection = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, payload = _a.payload;
            return collectionsAdminApi.update(id, payload);
        },
        onSuccess: function (_, _a) {
            var id = _a.id;
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(id) });
        },
    });
};
export var useSetVerification = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, verified = _a.verified;
            return collectionsAdminApi.setVerification(id, verified);
        },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
export var useSetCollectionType = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, type = _a.type;
            return collectionsAdminApi.setType(id, type);
        },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
export var useDeleteCollection = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (id) { return collectionsAdminApi.deleteOne(id); },
        onSuccess: function () { return qc.invalidateQueries({ queryKey: KEYS.all }); },
    });
};
var mintStatusKey = function (id) {
    return ["admin", "collections", id, "mint-status"];
};
// Пока минт идёт (MINTING) — поллим статус каждые 4с, иначе не перезапрашиваем.
export var useCollectionMintStatus = function (id) {
    return useQuery({
        queryKey: mintStatusKey(id),
        queryFn: function () { return collectionsAdminApi.getMintStatus(id); },
        enabled: !!id,
        retry: retryNon4xx,
        refetchInterval: function (query) { var _a; return ((_a = query.state.data) === null || _a === void 0 ? void 0 : _a.mintStatus) === "MINTING" ? 4000 : false; },
    });
};
export var useMintCollection = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (id) { return collectionsAdminApi.mint(id); },
        onSuccess: function (data, id) {
            qc.setQueryData(mintStatusKey(id), data);
            qc.invalidateQueries({ queryKey: mintStatusKey(id) });
            qc.invalidateQueries({ queryKey: KEYS.one(id) });
        },
    });
};
